/**
 * Transcription Work Page
 *
 * Split view: Audio/study on left, report form on right
 * For TRANSCRIPTOR role users
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Save,
  Send,
  FileText,
  Image,
} from 'lucide-react'

import {
  getReportById,
  updateReport,
  submitForReview,
  formatReportDateTime,
  getReportStatusVariant,
} from '@/application/reports'
import { useStudy } from '@/application/studies'
import { DictadoGrabado } from '@/presentation/components/dictation/DictadoGrabado'
import { APP_ROUTES, REPORT_STATUS_LABELS } from '@/config/constants'
import { VoiceDictationButton } from '@/presentation/components/VoiceDictation'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'

// =============================================================================
// Audio Player Component
// =============================================================================

// =============================================================================
// Study Info Component
// =============================================================================

function StudyInfo({ studyId }: { studyId: string }) {
  const { data: study, isLoading } = useStudy(studyId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  if (!study) {
    return (
      <div className="text-center py-8 text-[var(--foreground-muted)]">
        Estudio no encontrado
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
          <Image className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="font-medium">{study.studyDescription || 'Sin descripción'}</h3>
          <p className="text-sm text-[var(--foreground-muted)]">
            {study.modality} • {new Date(study.studyDate).toLocaleDateString('es-CO')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-[var(--foreground-muted)]">Accession</p>
          <p className="font-medium">{study.accessionNumber || '-'}</p>
        </div>
        <div>
          <p className="text-[var(--foreground-muted)]">Médico Referente</p>
          <p className="font-medium">{study.referringPhysician || '-'}</p>
        </div>
        <div>
          <p className="text-[var(--foreground-muted)]">Parte del Cuerpo</p>
          <p className="font-medium">{study.bodyPartExamined || '-'}</p>
        </div>
        <div>
          <p className="text-[var(--foreground-muted)]">Series/Imágenes</p>
          <p className="font-medium">
            {study.numberOfSeries} / {study.numberOfInstances}
          </p>
        </div>
      </div>

      <Link to={`/viewer/${study.id}`} target="_blank">
        <Button variant="outline" className="w-full">
          <Image className="h-4 w-4 mr-2" />
          Ver Imágenes
        </Button>
      </Link>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function TranscriptionWorkPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    findings: '',
    impression: '',
    recommendations: '',
    transcriptionNotes: '',
  })
  const [isSaving, setIsSaving] = useState(false)

  // Query for report
  const {
    data: report,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['report', id],
    queryFn: () => getReportById(id!),
    enabled: !!id,
  })

  // Initialize form with report data
  useEffect(() => {
    if (report) {
      setFormData({
        title: report.title || '',
        findings: report.findings || '',
        impression: report.impression || '',
        recommendations: report.recommendations || '',
        transcriptionNotes: report.transcriptionNotes || '',
      })
    }
  }, [report])

  // Mutation for saving (update report)
  const saveMutation = useMutation({
    mutationFn: () =>
      updateReport(id!, {
        title: formData.title,
        findings: formData.findings,
        impression: formData.impression,
        recommendations: formData.recommendations || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', id] })
      queryClient.invalidateQueries({ queryKey: ['transcription-queue'] })
    },
    onSettled: () => {
      setIsSaving(false)
    },
  })

  // Mutation for submitting for review
  const submitMutation = useMutation({
    mutationFn: () => submitForReview(id!, formData.transcriptionNotes || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', id] })
      queryClient.invalidateQueries({ queryKey: ['transcription-queue'] })
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      // Navigate back to queue
      navigate(APP_ROUTES.TRANSCRIPTION.QUEUE)
    },
  })

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = () => {
    setIsSaving(true)
    saveMutation.mutate()
  }

  const handleSubmit = () => {
    if (!formData.findings.trim() || !formData.impression.trim()) {
      alert('Los hallazgos e impresión son requeridos')
      return
    }
    submitMutation.mutate()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  if (isError || !report) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-red-500">{error?.message || 'Informe no encontrado'}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate(APP_ROUTES.TRANSCRIPTION.QUEUE)}
        >
          Volver a la Cola
        </Button>
      </div>
    )
  }

  // Check if report can be edited (TRANSCRIBING status)
  const canEdit = report.status === 'TRANSCRIBING'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(APP_ROUTES.TRANSCRIPTION.QUEUE)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-[var(--foreground)]">
                Transcripción
              </h1>
              <Badge variant={getReportStatusVariant(report.status)}>
                {REPORT_STATUS_LABELS[report.status]}
              </Badge>
            </div>
            <p className="text-sm text-[var(--foreground-muted)]">
              Creado: {formatReportDateTime(report.createdAt)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={isSaving || saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Guardar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitMutation.isPending}
                title="El informe vuelve al especialista, que es quien lo firma"
              >
                {submitMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Solicitar firma
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Study Info & Audio */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Información del Estudio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StudyInfo studyId={report.studyId} />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <DictadoGrabado
                studyId={report.studyId}
                onTexto={(texto) =>
                  setFormData((previo) => ({
                    ...previo,
                    findings: previo.findings ? `${previo.findings}\n${texto}` : texto,
                  }))
                }
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Report Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">
                Contenido del Informe
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Title */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground)] mb-2 block">
                  Título del Informe
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                  placeholder="Título del informe..."
                  disabled={!canEdit}
                />
              </div>

              {/* Findings */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-[var(--foreground)]">
                    Hallazgos *
                  </label>
                  {canEdit && (
                    <VoiceDictationButton
                      onTranscript={(text) =>
                        handleChange('findings', formData.findings + ' ' + text)
                      }
                    />
                  )}
                </div>
                <textarea
                  value={formData.findings}
                  onChange={(e) => handleChange('findings', e.target.value)}
                  className="w-full min-h-[150px] rounded-md border border-[var(--border)] bg-[var(--background)] p-3 text-sm resize-y"
                  placeholder="Describa los hallazgos radiológicos..."
                  disabled={!canEdit}
                />
              </div>

              {/* Impression */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-[var(--foreground)]">
                    Impresión Diagnóstica *
                  </label>
                  {canEdit && (
                    <VoiceDictationButton
                      onTranscript={(text) =>
                        handleChange('impression', formData.impression + ' ' + text)
                      }
                    />
                  )}
                </div>
                <textarea
                  value={formData.impression}
                  onChange={(e) => handleChange('impression', e.target.value)}
                  className="w-full min-h-[100px] rounded-md border border-[var(--border)] bg-[var(--background)] p-3 text-sm resize-y"
                  placeholder="Impresión diagnóstica..."
                  disabled={!canEdit}
                />
              </div>

              {/* Recommendations */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-[var(--foreground)]">
                    Recomendaciones
                  </label>
                  {canEdit && (
                    <VoiceDictationButton
                      onTranscript={(text) =>
                        handleChange('recommendations', formData.recommendations + ' ' + text)
                      }
                    />
                  )}
                </div>
                <textarea
                  value={formData.recommendations}
                  onChange={(e) => handleChange('recommendations', e.target.value)}
                  className="w-full min-h-[80px] rounded-md border border-[var(--border)] bg-[var(--background)] p-3 text-sm resize-y"
                  placeholder="Recomendaciones opcionales..."
                  disabled={!canEdit}
                />
              </div>

              {/* Transcription Notes */}
              <div>
                <label className="text-sm font-medium text-[var(--foreground)] mb-2 block">
                  Notas de Transcripción
                </label>
                <textarea
                  value={formData.transcriptionNotes}
                  onChange={(e) => handleChange('transcriptionNotes', e.target.value)}
                  className="w-full min-h-[60px] rounded-md border border-[var(--border)] bg-[var(--background)] p-3 text-sm resize-y"
                  placeholder="Notas internas sobre la transcripción (opcional)..."
                  disabled={!canEdit}
                />
                <p className="text-xs text-[var(--foreground-muted)] mt-1">
                  Estas notas son visibles solo para el equipo de revisión
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
